import { Link } from 'react-router-dom'
import {
  LegalPageLayout,
  LegalSection,
  LegalSubsection,
  LegalParagraph,
  LegalList,
} from '@roselabs-io/saas-shell'
import { Header, Footer } from '../components'

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="November 19, 2025"
      header={<Header />}
      footer={<Footer />}
      className="bg-slate-300"
    >
      <LegalSection number="1" title="Introduction">
        <LegalParagraph>
          At Falcon, we take your privacy seriously. This Privacy Policy explains how we collect,
          use, disclose, and safeguard your information when you use our vulnerability scanning
          service.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="2" title="Information We Collect">
        <LegalSubsection number="2.1" title="Account Information">
          <LegalParagraph>When you register, we collect:</LegalParagraph>
          <LegalList>
            <li>Email address</li>
            <li>Name (optional)</li>
            <li>Password (encrypted)</li>
            <li>OAuth provider information (if using social login)</li>
          </LegalList>
        </LegalSubsection>

        <LegalSubsection number="2.2" title="Scanning Data">
          <LegalParagraph>When you scan your projects, we collect and store:</LegalParagraph>
          <LegalList>
            <li>Lockfile contents (package-lock.json, requirements.txt, etc.)</li>
            <li>Dependency names and versions</li>
            <li>Scan results and vulnerability data</li>
            <li>Service names and configurations</li>
          </LegalList>
          <LegalParagraph>
            <strong>Important:</strong> We do NOT collect or store your source code. We only process
            lockfiles which contain dependency information.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection number="2.3" title="Usage Data">
          <LegalParagraph>We automatically collect:</LegalParagraph>
          <LegalList>
            <li>IP address and general location</li>
            <li>Browser type and device information</li>
            <li>Pages visited and features used</li>
            <li>API usage and scan frequency</li>
          </LegalList>
        </LegalSubsection>

        <LegalSubsection number="2.4" title="Payment Information">
          <LegalParagraph>
            Payment processing is handled by our payment provider (Stripe). We do not store credit
            card numbers. We only receive:
          </LegalParagraph>
          <LegalList>
            <li>Last four digits of your card</li>
            <li>Card expiration date</li>
            <li>Billing address</li>
          </LegalList>
        </LegalSubsection>
      </LegalSection>

      <LegalSection number="3" title="How We Use Your Information">
        <LegalParagraph>We use your information to:</LegalParagraph>
        <LegalList>
          <li>Provide and maintain the Service</li>
          <li>Send vulnerability alerts to your specified email addresses</li>
          <li>Process payments and manage subscriptions</li>
          <li>Improve our vulnerability detection</li>
          <li>Send service updates and security notices</li>
          <li>Respond to support requests</li>
          <li>Analyze usage patterns to improve the Service</li>
        </LegalList>
      </LegalSection>

      <LegalSection number="4" title="Alert Recipients">
        <LegalParagraph>
          You can configure alert profiles to deliver notifications via email, Slack, or webhooks.
          Email addresses and webhook URLs are stored to deliver alerts. Recipients do not need
          Falcon accounts—they only receive the alerts you configure.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="5" title="Data Sharing">
        <LegalParagraph>
          We do not sell your personal information. We may share data with:
        </LegalParagraph>
        <LegalList>
          <li>
            <strong>Service providers:</strong> Hosting, payment processing, email delivery
          </li>
          <li>
            <strong>Analytics providers:</strong> Aggregated, anonymized usage data
          </li>
          <li>
            <strong>Legal requirements:</strong> When required by law or to protect rights
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection number="6" title="Data Security">
        <LegalParagraph>We implement security measures including:</LegalParagraph>
        <LegalList>
          <li>Encryption in transit (TLS) and at rest</li>
          <li>Secure password hashing</li>
          <li>Regular security audits</li>
          <li>Access controls and monitoring</li>
        </LegalList>
      </LegalSection>

      <LegalSection number="7" title="Data Retention">
        <LegalParagraph>We retain your data as follows:</LegalParagraph>
        <LegalList>
          <li>Account data: Until account deletion</li>
          <li>Scan history: Until account deletion or as specified in your plan</li>
          <li>Lockfile data: Processed for scanning, stored for history</li>
          <li>After account deletion: Data removed within 30 days</li>
        </LegalList>
      </LegalSection>

      <LegalSection number="8" title="Your Rights">
        <LegalParagraph>You have the right to:</LegalParagraph>
        <LegalList>
          <li>Access your personal data</li>
          <li>Correct inaccurate data</li>
          <li>Delete your account and data</li>
          <li>Export your data</li>
          <li>Opt out of marketing communications</li>
        </LegalList>
        <LegalParagraph>
          To exercise these rights,{' '}
          <Link to="/contact" className="text-accent-600 hover:text-accent-700">
            contact us
          </Link>{' '}
          through our contact form with subject "Privacy".
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="9" title="Cookies">
        <LegalParagraph>
          We use essential cookies for authentication and session management. We may use analytics
          cookies to understand usage. You can control cookies through your browser settings.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="10" title="International Transfers">
        <LegalParagraph>
          Your data may be transferred to and processed in countries other than your own. We ensure
          appropriate safeguards are in place for such transfers.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="11" title="Children's Privacy">
        <LegalParagraph>
          The Service is not intended for users under 13. We do not knowingly collect information
          from children.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="12" title="Changes to This Policy">
        <LegalParagraph>
          We may update this policy from time to time. We will notify you of material changes via
          email or through the Service.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number="13" title="Contact Us">
        <LegalParagraph>
          If you have questions about this privacy policy,{' '}
          <Link to="/contact" className="text-accent-600 hover:text-accent-700 transition-colors">
            contact us
          </Link>{' '}
          through our contact form with subject "Privacy".
        </LegalParagraph>
      </LegalSection>
    </LegalPageLayout>
  )
}
